import React, { ReactNode, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Input,
  message,
  Modal,
  notification,
  Switch,
  Typography,
} from '@duxton-web/ui';

import Service, {
  Rollout,
  RolloutDiffCreateRequest,
  RolloutReview,
  RolloutTarget,
  ValidationError,
} from '@/lib/App';

import './SaveButtonSection.css';

import { WarningAltOutlined } from '@duxton-web/icons';
import { parse, stringify } from 'lossless-json';

import {
  ROLLOUT_DESCRIPTION_MAX_LENGTH,
  ROLLOUT_REQUEST_LINK_MAX_LENGTH,
} from '@/common/const';
import { getDomainFamily } from '@/common/domains';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import { FeedbackType, openInAppFeedback } from '@/common/inAppFeedback';
import RolloutImpactSummary from '@/common/RolloutImpactSummary';
import { HTTP_STATUS, VariableState } from '@/constants';
import { useRolloutContext } from '@/contexts/RolloutContext';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import { useVariable } from '@/contexts/VariableContext';
import CanaryRolloutPlanSummary from '@/features/variable/canary-rollout-plan-summary';
import { isCanaryEligible } from '@/features/variable/settings/canary';
import { useGetCanaryPilotTechFamilies } from '@/hooks';
import { RolloutDiff as ChangelogRolloutDiff } from '@/pages/Changelog/ChangelogItem';
import CanaryRolloutCheckModal from '@/pages/RolloutTab/SaveButtonSection/CanaryRolloutCheckModal';
import { hasValidationMessages } from '@/pages/RolloutTab/SaveButtonSection/validationMessages';
import { UserAction } from '@/utils/accessControl';
import { getModifiedRolloutTargetsMap } from '@/utils/changelog';
import { appendEnvParam, useAppNavigate } from '@/utils/navigation';
import { removeLosslessObject } from '@/utils/object';
import { getRolloutTargetName } from '@/utils/rollout';
import { formatHasAccessErrorGeneric, JIRA_URL } from '@/utils/url';

interface SaveButtonSectionProps {
  initialRollout: Rollout;
  disabled?: boolean;
  invalidRolloutTargetsIndex?: number[];
  inproperInstrumentedFacetsIndex?: number[];
  // rollout, pendingApprovals, isEditRolloutButtonClicked, isSaving now come from context
}

const SaveButtonSection = ({
  initialRollout,
  disabled,
  invalidRolloutTargetsIndex,
  inproperInstrumentedFacetsIndex,
}: SaveButtonSectionProps) => {
  const { navigateTo } = useAppNavigate();
  // Get variable and rollout state from context
  const variable = useVariable();
  const {
    rollout,
    pendingApprovals,
    isEditRolloutButtonClicked,
    handleSaving,
  } = useRolloutContext();

  const [visible, setVisible] = useState<boolean>(false);
  const [confirmLoading] = useState<boolean>(false);
  const [validateLoading, setValidateLoading] = useState<boolean>(false);
  const [review, setReview] = useState<RolloutReview | null>(null);

  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const [togglingShowAllRollout, setTogglingShowAllRollout] = useState(false);

  const [rolloutDescription, setRolloutDescription] = useState<string>('');
  const [rolloutTicket, setRolloutTicket] = useState<string>('');
  const [inproperFacetsConfirmation, setInproperFacetsConfirmation] =
    useState<boolean>(false);
  const [canaryCheckOpen, setCanaryCheckOpen] = useState<boolean>(false);
  // Track whether the user already submitted a skip reason this session.
  // The Redux variable state won't reflect the saved disabledReason until the
  // page refetches, so without this guard the canary prompt would reappear if
  // the user cancels the rollout modal and clicks Validate & Rollout again.
  const skipReasonSavedRef = useRef(false);
  const { permissionUtils } = useUserRolesContext();

  // Canary eligibility gate: show the canary check modal when the variable is
  // eligible (assignBy ∈ pax/mex/dax), pilot-enrolled, canary is off, and the
  // user has not yet provided a reason for skipping canary.
  const { data: canaryPilotConfig, isLoading: isCanaryPilotLoading } =
    useGetCanaryPilotTechFamilies();
  const isCanaryPilotEnrolled = (() => {
    if (!canaryPilotConfig) return false;
    if (canaryPilotConfig.usePilotTechFamilies === false) return true;
    if (!variable?.techFamily) return false;
    return canaryPilotConfig.techFamilies.includes(variable.techFamily);
  })();
  const canaryConfig = variable?.metadata?.canaryConfig;
  const shouldShowCanaryCheck =
    !skipReasonSavedRef.current &&
    isCanaryEligible(variable) &&
    isCanaryPilotEnrolled &&
    !canaryConfig?.enabled &&
    !variable?.canary?.enabled &&
    !canaryConfig?.disabledReason;

  function formatErrorMessage(e: any) {
    if (e.response && e.response.status === HTTP_STATUS.FORBIDDEN) {
      return formatHasAccessErrorGeneric(e);
    }
    if (e?.response?.data?.arg && e.response.status != HTTP_STATUS.FORBIDDEN) {
      const message: string = e.response.data.arg;
      return message.replace('\n', ', ');
    }

    return 'unknown error';
  }

  function renderValidationErrors(errors: ValidationError[]): ReactNode {
    if (!errors) return false;

    return errors.map((vError, index) => {
      if (vError?.failure?.includes('json validation failed:')) return false;

      return (
        <li key={index}>
          {vError.failure}
          {vError.violations && vError.violations.length > 0 && (
            <ul className="violations-list">
              {vError.violations.map((v, index) => (
                <li key={index}>{v}</li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  }

  function renderWarningMessages(): ReactNode {
    const { warnings } = review;
    let warningMessages: ValidationError[] | undefined = warnings;

    if (!warningMessages?.length) return false;

    return (
      <ul className="failures-list">
        {renderValidationErrors(warningMessages)}
      </ul>
    );
  }

  function renderInproperInstrumentFacetsMessages(): ReactNode {
    if (!inproperInstrumentedFacetsIndex?.length) return false;

    return (
      <Flex vertical>
        <Typography.Paragraph>
          There is no data available for the facets of the constraints marked in
          exclamation. If they are not implemented in code, these facets will
          have no effect.
        </Typography.Paragraph>
        <Typography.Text>
          Rollout targets affected:
          <br />
        </Typography.Text>
        <ul className="failures-list">
          {inproperInstrumentedFacetsIndex.map((index) => {
            if (index === 0)
              return (
                <li key="rollout-default">
                  <Typography.Link
                    onClick={() => {
                      navigateTo(`?expand=0`, { replace: true });

                      setVisible(false);
                    }}
                  >
                    Everything Else (Rollout Default)
                  </Typography.Link>
                </li>
              );
            else
              return (
                <li key={`rollout-${index - 1}`}>
                  <Typography.Link
                    onClick={() => {
                      navigateTo(`?expand=${index}`, { replace: true });

                      setVisible(false);
                    }}
                  >
                    {`${index}: ${getRolloutTargetName(
                      rollout?.rollouts[index - 1].constraints,
                    )}`}
                  </Typography.Link>
                </li>
              );
          })}
        </ul>
      </Flex>
    );
  }

  function renderInproperInstrumentFacetsConfirmation(): ReactNode {
    const { errors } = review;
    let errorMessages: ValidationError[] | undefined = errors;
    let isError = errorMessages?.length || invalidRolloutTargetsIndex?.length;

    if (isError || !inproperInstrumentedFacetsIndex?.length) return false;

    return (
      <Checkbox
        title="I have confirmed that these facets are indeed implemented in code, and will work as normal"
        onChange={(e) => {
          setInproperFacetsConfirmation(e.target.checked);
        }}
      >
        I have confirmed that these facets are indeed implemented in code, and
        will work as normal
      </Checkbox>
    );
  }

  function renderWarningContent(): ReactNode {
    const { warnings } = review;
    let warningMessages: ValidationError[] | undefined = warnings;

    if (!warningMessages?.length && !inproperInstrumentedFacetsIndex?.length)
      return false;

    return (
      <Alert
        type="warning"
        description={
          <Flex gap={8} vertical>
            {renderWarningMessages()}
            {renderInproperInstrumentFacetsMessages()}
            {renderInproperInstrumentFacetsConfirmation()}
          </Flex>
        }
      />
    );
  }

  function renderErrorMessages(errorMessages: ValidationError[]): ReactNode {
    if (!errorMessages?.length && !invalidRolloutTargetsIndex?.length)
      return false;

    return (
      <Alert
        type="error"
        description={
          <ul className="failures-list">
            {renderValidationErrors(errorMessages)}
            {invalidRolloutTargetsIndex.map((index) => {
              if (index === 0)
                return (
                  <li key="rollout-default">
                    <Typography.Link
                      onClick={() => {
                        navigateTo(`?expand=0`, { replace: true });

                        setVisible(false);
                      }}
                    >
                      Everything Else (Rollout Default)
                    </Typography.Link>
                  </li>
                );
              else
                return (
                  <li key={`rollout-${index - 1}`}>
                    <Typography.Link
                      onClick={() => {
                        navigateTo(`?expand=${index}`, { replace: true });

                        setVisible(false);
                      }}
                    >
                      {`${getRolloutTargetName(
                        rollout?.rollouts[index - 1].constraints,
                      )}: invalid value`}
                    </Typography.Link>
                  </li>
                );
            })}
          </ul>
        }
      />
    );
  }

  function handleValidateAndRolloutClick(): void {
    if (isCanaryPilotLoading) return;
    if (shouldShowCanaryCheck) {
      setCanaryCheckOpen(true);
      return;
    }
    showModal();
  }

  async function showModal(): Promise<void> {
    setValidateLoading(true);

    try {
      let response = await Service.planner!.reviewRollout(rollout);
      setVisible(true);
      setValidateLoading(false);
      setReview(response.data);
    } catch (e) {
      if (e.response) {
        if (e.response.status == 504) {
          message.error(
            'Server Timeout. Please click Validate and Rollout button again',
          );
          setValidateLoading(false);
          return;
        }
        if (e.response.status == 400) {
          message.error(e.response.data?.devMessage || 'Validation failed');
          setValidateLoading(false);
          return;
        }
      }
      console.error(e);
    }
  }

  function onCancel(): void {
    setVisible(false);
    setReview(null);
  }

  async function handleCreateApprovalRequest(): Promise<void> {
    const { name } = variable;

    const diff: RolloutDiffCreateRequest = {
      oldRollout: initialRollout,
      newRollout: rollout,
      description: rolloutDescription,
    };

    if (rolloutTicket) {
      diff.requestLink = `${JIRA_URL}${rolloutTicket}`;
    }

    try {
      if (isEditRolloutButtonClicked) {
        const { id: diffId, oldRollout, newRollout } = pendingApprovals[0];
        // It is possible for 'oldRollout.variable' to be an empty string
        // (i.e when the rollout is newly created)
        await Service.planner!.declineRollout(
          oldRollout.variable ? oldRollout.variable : newRollout.variable,
          diffId,
        );
      }
    } catch (e) {
      notification.error({
        message: 'Rollout Edits',
        description: `We were unable to process your edited rollout changes.`,
        duration: 5,
      });

      setVisible(false);
      return;
    }

    try {
      const rolloutDiff = await Service.planner!.createRolloutDiff(name, diff);
      let link: string | URL | undefined;
      link = appendEnvParam(
        `${window.location.origin}/${getDomainFamily()}/variables/${
          variable.name
        }/rollout/approve-request/${rolloutDiff.id}`,
      );

      handleSaving(false, true);
      window.open(link, '_blank');
      openInAppFeedback({
        type: FeedbackType.NPS,
        context: 'rolloutPage',
        event: 'rolloutCreated',
      });
    } catch (e) {
      notification.error({
        description: formatErrorMessage(e),
        message: 'Creation of Approval request',
      });
    }

    setVisible(false);
  }

  function renderShowAllButton(): ReactNode {
    return (
      <Flex
        gap={8}
        align="center"
        style={{
          padding: '6px 14px',
          borderRadius: 48,
        }}
      >
        <Switch
          checked={includeUnchanged}
          onChange={(checked) => {
            setIncludeUnchanged(checked);
            setTogglingShowAllRollout(true);
            setTimeout(() => setTogglingShowAllRollout(false), 1000);
          }}
          loading={validateLoading || togglingShowAllRollout}
          disabled={validateLoading || togglingShowAllRollout}
        />
        <Typography.Body level={3} style={{ lineHeight: 0 }} type="secondary">
          Show all rollouts
        </Typography.Body>
      </Flex>
    );
  }

  function renderTitle(): ReactNode {
    return (
      <Flex justify="space-between" align="center">
        <Typography.Body type="secondary" level={3}>
          Changes Made
        </Typography.Body>
        {renderShowAllButton()}
      </Flex>
    );
  }

  function enrichRollout(rollout): void {
    if (!rollout?.rollouts?.length) return;

    rollout?.rollouts.forEach((target: RolloutTarget, index: number) => {
      // we are assuming that new target will not have id before they are save in storer
      if (!target?.id) {
        target.id = `newrollout-target-${index}`;
      }
    });
  }

  function renderRolloutDiff(
    rolloutTargets: Map<string, ModifiedRolloutTargetMapValue>,
  ): ReactNode {
    return (
      <Flex gap={8} vertical>
        {renderTitle()}
        <ChangelogRolloutDiff
          rolloutTargets={rolloutTargets}
          variable={variable}
        />
      </Flex>
    );
  }

  function renderRolloutRequestDescription(): ReactNode {
    return (
      <Flex gap={8} vertical style={{ marginBottom: 16 }}>
        <Typography.Body type="secondary" level={3}>
          Description (required)
        </Typography.Body>
        <Input
          value={rolloutDescription}
          showCount
          onChange={(e) => {
            setRolloutDescription(e.target.value);
          }}
          placeholder="Describe the change"
          maxLength={ROLLOUT_DESCRIPTION_MAX_LENGTH}
        />
        <Typography.Body type="secondary" level={3}>
          Jira Ticket (Optional)
        </Typography.Body>
        <Input
          addonBefore={JIRA_URL}
          value={rolloutTicket}
          showCount
          onChange={(e) => {
            setRolloutTicket(e.target.value);
          }}
          maxLength={ROLLOUT_REQUEST_LINK_MAX_LENGTH - JIRA_URL.length}
          placeholder="Add ticket number"
        />
      </Flex>
    );
  }

  function getModalContent(): ReactNode {
    if (!review) return false;

    const { errors, warnings } = review;
    let errorMessages: ValidationError[] | undefined = errors;
    let warningMessages: ValidationError[] | undefined = warnings;
    const isError = hasValidationMessages(
      errorMessages,
      invalidRolloutTargetsIndex,
    );
    const isWarning = hasValidationMessages(
      warningMessages,
      inproperInstrumentedFacetsIndex,
    );

    if (isError) {
      if (!isWarning) return renderErrorMessages(errorMessages ?? []);
      return (
        <Flex vertical gap={8}>
          <div>{renderErrorMessages(errorMessages ?? [])}</div>
          <div>{renderWarningContent()}</div>
        </Flex>
      );
    }

    const rolloutClone = parse(stringify(rollout)) as Rollout;
    removeLosslessObject(rolloutClone);
    enrichRollout(rolloutClone);

    const rolloutTarget = getModifiedRolloutTargetsMap(
      rolloutClone,
      initialRollout,
      includeUnchanged,
    );

    return (
      <Flex vertical gap={8}>
        {isWarning && renderWarningContent()}
        {renderRolloutRequestDescription()}
        <RolloutImpactSummary rolloutTarget={rolloutTarget} />
        <CanaryRolloutPlanSummary variable={variable} />
        {renderRolloutDiff(rolloutTarget)}
      </Flex>
    );
  }

  function getModelTitle(): string {
    if (!review) return '';

    const { errors } = review;
    let errorMessages: ValidationError[] | undefined = errors;
    const isError = hasValidationMessages(
      errorMessages,
      invalidRolloutTargetsIndex,
    );

    if (isError) return 'Validation Failed';

    return 'Validation Passed';
  }

  function renderModal(): ReactNode {
    if (!review) return false;

    const { errors } = review;
    let errorMessages: ValidationError[] | undefined = errors;
    const isError = hasValidationMessages(
      errorMessages,
      invalidRolloutTargetsIndex,
    );

    return (
      <Modal
        centered
        title={getModelTitle()}
        open={visible}
        onCancel={onCancel} // this is to enable the auto dismiss when clicking outside of modal
        cancelButtonProps={{ size: 'small' }}
        confirmLoading={confirmLoading}
        okButtonProps={{
          hidden: Boolean(isError),
          size: 'small',
          disabled:
            rolloutDescription.length === 0 ||
            (inproperInstrumentedFacetsIndex?.length &&
              !inproperFacetsConfirmation),
        }}
        okText="Request Change Approval"
        onOk={handleCreateApprovalRequest}
        width={900}
        destroyOnClose
        styles={{
          body: {
            height: 'calc(100vh - 200px)',
            overflowX: 'hidden',
            overflowY: 'auto',
          },
        }}
      >
        {getModalContent()}
      </Modal>
    );
  }

  if (variable.state === VariableState.ARCHIVED) return null;

  if (
    !permissionUtils.canPerformAction(
      UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
      [variable.techFamily],
    )
  ) {
    return null;
  }

  return (
    <div className="save_button_section">
      {renderModal()}
      <CanaryRolloutCheckModal
        open={canaryCheckOpen}
        variable={variable}
        onClose={() => setCanaryCheckOpen(false)}
        onProceed={() => {
          skipReasonSavedRef.current = true;
          setCanaryCheckOpen(false);
          showModal();
        }}
      />
      <Button
        data-testid="savebuttonsection-validateaandrollout"
        type="secondary"
        danger={invalidRolloutTargetsIndex?.length > 0}
        size="small"
        shape="round"
        onClick={handleValidateAndRolloutClick}
        loading={validateLoading}
        disabled={disabled}
        className="save-button"
        icon={
          invalidRolloutTargetsIndex?.length > 0 ? <WarningAltOutlined /> : null
        }
      >
        Validate and Rollout
      </Button>
    </div>
  );
};

export default SaveButtonSection;
